import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FitIcon,
} from '@mui/icons-material';

import DocumentPageRenderer, { BoundingBoxData } from './DocumentPageRenderer';
import BoundingBoxZoomModal from './BoundingBoxZoomModal';
import { DetailedEvidenceBox } from '../../types/project';

interface EvidencePageViewerProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  pageNumber: number;
  allPages: number[];
  boundingBoxes?: BoundingBoxData;
  measurementFound?: string;
  measurementLocation?: string;
  requirementTitle?: string;
  complianceStatus?: string;
  onPageChange?: (page: number) => void;
}

const EvidencePageViewer: React.FC<EvidencePageViewerProps> = ({
  open,
  onClose,
  documentId,
  documentName,
  pageNumber,
  allPages = [],
  boundingBoxes,
  measurementFound,
  measurementLocation,
  requirementTitle,
  complianceStatus,
  onPageChange
}) => {
  const [imageWidth, setImageWidth] = useState(800);

  // Bounding box zoom modal state
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedBox, setSelectedBox] = useState<number[] | null>(null);
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(1);
  const [totalBoxesOnPage, setTotalBoxesOnPage] = useState(0);

  // Helper function to normalize bounding boxes for a page
  const getNormalizedPageBoxes = (pageNum: number): number[][] => {
    if (!boundingBoxes) return [];

    const pageKey = `page_${pageNum}`;
    const pageNumber_str = pageNum.toString();
    const rawPageBoxes = boundingBoxes[pageKey] || boundingBoxes[pageNumber_str] || [];

    // Handle DetailedEvidenceBox format
    if (rawPageBoxes.length > 0 && typeof rawPageBoxes[0] === 'object' && 'coordinates' in rawPageBoxes[0]) {
      return (rawPageBoxes as DetailedEvidenceBox[]).map(box => box.coordinates);
    }

    // Handle legacy single box format [x1,y1,x2,y2]
    if (rawPageBoxes.length === 4 && typeof rawPageBoxes[0] === 'number') {
      return [rawPageBoxes as unknown as number[]];
    }

    // Handle legacy array format [[x1,y1,x2,y2], [x1,y1,x2,y2]]
    return rawPageBoxes as number[][];
  };

  // Sort pages for navigation - handle empty allPages gracefully
  const sortedPages = allPages && allPages.length > 0 ? [...allPages].sort((a, b) => a - b) : [pageNumber];
  const currentIndex = sortedPages.indexOf(pageNumber);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < sortedPages.length - 1;

  // Add validation warning for invalid page setup
  React.useEffect(() => {
    if (allPages && allPages.length > 0 && !allPages.includes(pageNumber)) {
      console.warn(
        `EvidencePageViewer: Current page ${pageNumber} not found in allPages [${allPages.join(', ')}]. ` +
        `This may indicate a page mapping issue.`
      );
    }
  }, [pageNumber, allPages]);

  const handlePrevPage = () => {
    if (canGoPrev && onPageChange) {
      onPageChange(sortedPages[currentIndex - 1]);
    }
  };

  const handleNextPage = () => {
    if (canGoNext && onPageChange) {
      onPageChange(sortedPages[currentIndex + 1]);
    }
  };

  const handleZoomIn = () => {
    setImageWidth(prev => Math.min(prev * 1.25, 1600));
  };

  const handleZoomOut = () => {
    setImageWidth(prev => Math.max(prev * 0.8, 400));
  };

  const handleFitToWidth = () => {
    setImageWidth(800);
  };

  const getStatusColor = () => {
    switch (complianceStatus) {
      case 'compliant':
        return 'success';
      case 'non_compliant':
        return 'error';
      case 'not_applicable':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = () => {
    switch (complianceStatus) {
      case 'compliant':
        return '✓';
      case 'non_compliant':
        return '✗';
      case 'not_applicable':
        return 'N/A';
      default:
        return '?';
    }
  };

  // Calculate total boxes on current page
  React.useEffect(() => {
    const pageBoxes = getNormalizedPageBoxes(pageNumber);
    setTotalBoxesOnPage(pageBoxes.length);
  }, [boundingBoxes, pageNumber]);

  // Handle bounding box click
  const handleBoundingBoxClick = (box: number[], boxIndex: number) => {
    setSelectedBox(box);
    setSelectedBoxIndex(boxIndex);
    setZoomModalOpen(true);
  };

  // Handle navigation between bounding boxes on the same page
  const handleNavigateBox = (direction: 'prev' | 'next') => {
    if (!boundingBoxes || totalBoxesOnPage === 0) return;

    const pageBoxes = getNormalizedPageBoxes(pageNumber);

    let newIndex = selectedBoxIndex;
    if (direction === 'prev' && selectedBoxIndex > 1) {
      newIndex = selectedBoxIndex - 1;
    } else if (direction === 'next' && selectedBoxIndex < totalBoxesOnPage) {
      newIndex = selectedBoxIndex + 1;
    }

    if (newIndex !== selectedBoxIndex && pageBoxes[newIndex - 1]) {
      setSelectedBox(pageBoxes[newIndex - 1]);
      setSelectedBoxIndex(newIndex);
    }
  };

  const handleCloseZoomModal = () => {
    setZoomModalOpen(false);
    setSelectedBox(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '80vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1
        }}
      >
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Document Evidence
          </Typography>
          {requirementTitle && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
              {requirementTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2, overflow: 'hidden' }}>
        {/* Document and Page Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            📄 {documentName} • Page {pageNumber}
            {sortedPages.length > 1 && ` of ${sortedPages.length} evidence pages`}
          </Typography>

          {complianceStatus && (
            <Chip
              label={`${getStatusIcon()} ${complianceStatus.toUpperCase().replace('_', ' ')}`}
              color={getStatusColor() as any}
              size="small"
              variant="filled"
            />
          )}
        </Box>

        {/* Evidence Context */}
        {(measurementFound || measurementLocation) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {measurementFound && (
              <Typography variant="body2" sx={{ mb: measurementLocation ? 1 : 0 }}>
                <strong>Measurement Found:</strong> {measurementFound}
              </Typography>
            )}
            {measurementLocation && (
              <Typography variant="body2">
                <strong>Location:</strong> {measurementLocation}
              </Typography>
            )}
          </Alert>
        )}

        {/* Page Image with Scrollable Container */}
        <Box
          sx={{
            maxHeight: '60vh',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 1,
            p: 2
          }}
        >
          <DocumentPageRenderer
            documentId={documentId}
            pageNumber={pageNumber}
            boundingBoxes={boundingBoxes}
            width={imageWidth}
            onBoundingBoxClick={handleBoundingBoxClick}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrevIcon />}
              onClick={handlePrevPage}
              disabled={!canGoPrev}
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<NextIcon />}
              onClick={handleNextPage}
              disabled={!canGoNext}
            >
              Next
            </Button>
          </Box>

          {/* Zoom Controls */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Zoom:
            </Typography>
            <IconButton size="small" onClick={handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
            <IconButton size="small" onClick={handleFitToWidth}>
              <FitIcon />
            </IconButton>
            <IconButton size="small" onClick={handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>

      {/* Bounding Box Zoom Modal */}
      {selectedBox && (
        <BoundingBoxZoomModal
          open={zoomModalOpen}
          onClose={handleCloseZoomModal}
          documentId={documentId}
          documentName={documentName}
          pageNumber={pageNumber}
          boundingBox={selectedBox}
          boxIndex={selectedBoxIndex}
          totalBoxes={totalBoxesOnPage}
          requirementTitle={requirementTitle}
          measurementFound={measurementFound}
          onNavigateBox={handleNavigateBox}
        />
      )}
    </Dialog>
  );
};

export default EvidencePageViewer;